import { Box, useTheme } from "@mui/material";

/**
 * Trust mark for the honesty screen: the house chip idiom (InsightMark's
 * green-tint square) holding a padlock that clicks shut once on mount, with a
 * single ring pulse. Nothing loops — stillness reads as safety.
 */

interface PrivacyLockChipProps {
  size?: number;
}

export const PrivacyLockChip = ({ size = 56 }: PrivacyLockChipProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        width: size,
        height: size,
        "@keyframes lockShut": {
          from: { transform: "translateY(-3px)" },
          to: { transform: "translateY(0)" },
        },
        "@keyframes lockPulse": {
          "0%": { transform: "scale(1)", opacity: 0.5 },
          "100%": { transform: "scale(1.5)", opacity: 0 },
        },
        "& .lock-shackle": { animation: "lockShut 0.2s ease-out 0.3s backwards" },
        "& .lock-pulse": { animation: "lockPulse 0.9s ease-out 0.5s backwards", opacity: 0 },
        "@media (prefers-reduced-motion: reduce)": {
          "& .lock-shackle, & .lock-pulse": { animation: "none" },
        },
      }}
    >
      <Box
        className="lock-pulse"
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: `${Math.round(size * 0.28)}px`,
          border: "2px solid rgba(34, 197, 94, 0.4)",
        }}
      />
      <Box
        sx={{
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: `${Math.round(size * 0.28)}px`,
          backgroundColor: isDark ? "rgba(34, 197, 94, 0.15)" : "rgba(34, 197, 94, 0.1)",
          border: isDark
            ? "1px solid rgba(34, 197, 94, 0.3)"
            : "1px solid rgba(34, 197, 94, 0.2)",
        }}
      >
        <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="none">
          <path
            className="lock-shackle"
            d="M8 10 V7.5 a4 4 0 0 1 8 0 V10"
            stroke="#22c55e"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <rect x="5.5" y="10" width="13" height="9.5" rx="3" stroke="#22c55e" strokeWidth="2" />
          <circle cx="12" cy="14.75" r="1.5" fill="#22c55e" />
        </svg>
      </Box>
    </Box>
  );
};
